import {
  Box,
  Button,
  IconButton,
  Flex,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue
} from '@chakra-ui/react';
import { CheckIcon, CloseIcon } from '@chakra-ui/icons';
import Card from 'components/Card/Card';
import { useApproveWaitlist, useRejectWaitlist, useWaitlistsByStatus } from 'hooks/queries/useWaitlists';
import { WaitlistStatus } from 'models/Waitlist/types';

const PendingWaitlistTable = () => {
  const { data, isLoading, isError, refetch } = useWaitlistsByStatus(WaitlistStatus.PENDING);
  const approveWaitlist = useApproveWaitlist();
  const rejectWaitlist = useRejectWaitlist();

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const mutedTextColor = useColorModeValue('secondaryGray.600', 'secondaryGray.400');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const handleApprove = async (waitlistId: string) => {
    try {
      await approveWaitlist.mutateAsync(waitlistId);
    } catch {
      // Toast messaging handled in mutation onError
    }
  };

  const handleReject = async (waitlistId: string) => {
    try {
      await rejectWaitlist.mutateAsync({ waitlistId });
    } catch {
      // Toast messaging handled in mutation onError
    }
  };

  const isMutating = approveWaitlist.isPending || rejectWaitlist.isPending;

  return (
    <Card w="100%" p="0px">
      <Flex direction="column" p="20px" gap="12px">
        <Flex justify="space-between" align="center">
          <Text color={textColor} fontSize="lg" fontWeight="700">
            Pending Waitlist
          </Text>
        </Flex>
        {isLoading ? (
          <Flex align="center" justify="center" py="24px">
            <Spinner />
          </Flex>
        ) : isError ? (
          <Flex direction="column" align="center" justify="center" py="24px">
            <Text color={mutedTextColor} fontSize="sm" mb="12px">
              Failed to load waitlist entries.
            </Text>
            <Button size="sm" onClick={() => refetch()}>
              Retry
            </Button>
          </Flex>
        ) : data && data.length > 0 ? (
          <Box overflowX="auto" maxHeight="500px" overflowY="auto">
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th borderColor={borderColor} color={mutedTextColor} fontSize="xs" textTransform="uppercase">
                    Email
                  </Th>
                  <Th borderColor={borderColor} />
                </Tr>
              </Thead>
              <Tbody>
                {data.map((waitlist) => (
                  <Tr key={waitlist.id}>
                    <Td borderColor={borderColor} paddingRight={0} paddingLeft={0}>
                      <Text color={textColor} fontSize="sm" fontWeight="600">
                        {waitlist.email}
                      </Text>
                    </Td>
                    <Td borderColor={borderColor} textAlign="right" paddingLeft={2}>
                      <Flex gap="8px" justify="flex-end">
                        <IconButton
                          aria-label="Approve waitlist entry"
                          icon={<CheckIcon />}
                          colorScheme="green"
                          size="xs"
                          variant="solid"
                          isRound
                          isLoading={approveWaitlist.isPending && approveWaitlist.variables === waitlist.id}
                          isDisabled={isMutating}
                          onClick={() => handleApprove(waitlist.id)}
                        />
                        <IconButton
                          aria-label="Reject waitlist entry"
                          icon={<CloseIcon />}
                          colorScheme="red"
                          size="xs"
                          variant="solid"
                          isRound
                          isLoading={
                            rejectWaitlist.isPending &&
                            rejectWaitlist.variables?.waitlistId === waitlist.id
                          }
                          isDisabled={isMutating}
                          onClick={() => handleReject(waitlist.id)}
                        />
                      </Flex>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        ) : (
          <Flex align="center" justify="center" py="24px">
            <Text color={mutedTextColor} fontSize="sm">
              No pending waitlist entries.
            </Text>
          </Flex>
        )}
      </Flex>
    </Card>
  );
};

export default PendingWaitlistTable;
